import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { Container, Header, TopSaveWrapper } from "../formComponents.styles";
import {
  BlueButton,
  SaveButtonContainer,
  StyledForm,
} from "../../Admin.styles";
import {
  CheckInSection,
  extractQuestions,
  oneToThree,
} from "./checkInQuestions";
import { Question } from "./checkIn.types";
import { Api } from "../../../../services/api";
import { useHistory, useParams } from "react-router-dom";
import {
  CheckinType,
  EmployeeTitle,
} from "../../../../services/api/types/Members";
import { useMemberDetails } from "../../../../services/hooks/MemberDetails";
import { useAppSelector } from "../../../../store/hooks";
import { makeName } from "../../../../services/hooks/MembersList";
import { ErrorFocus } from "./errorScroll";

const questions: Array<Question> = [
  {
    type: "TextInput",
    label: "Team Member Name",
    name: "memberName",
    placeholder: "Name",
  },
  {
    type: "TextInput",
    label: "Team Leader Name",
    name: "leaderName",
    placeholder: "Name",
  },
  {
    type: "TextInput",
    label: "Reviewer Name",
    name: "reviewerName",
    placeholder: "Name",
  },
  {
    type: "TextInput",
    label: "Position",
    name: "position",
    placeholder: "Position",
  },
  { type: "DatePicker", name: "hireDate", label: "Hire Date" },
  { type: "DatePicker", name: "reviewDate", label: "Review Date" },
  {
    type: "RadioList",
    label:
      "On a scale from 1-3, (1 no, 3 yes), how was your training experience?",
    name: "trainingExperience",
    options: [...oneToThree],
    hasComments: true,
  },
  {
    type: "RadioList",
    label:
      "On a scale from 1-3, (1 no, 3 yes), do you feel like your team leader is passionate about your success?",
    name: "leaderSuccess",
    options: [...oneToThree],
    hasComments: true,
  },
  {
    type: "RadioList",
    label:
      "On a scale from 1-3, (1 no, 3 yes), does your team leader talk and communicate in a way you like?",
    name: "leaderCommunication",
    options: [...oneToThree],
    hasComments: true,
  },
  {
    type: "RadioList",
    label:
      "On a scale from 1-3, (1 no, 3 yes), do you see yourself working here in 12 months?",
    name: "workingHere",
    options: [...oneToThree],
    hasComments: true,
  },
  {
    type: "RadioList",
    label:
      "On a scale from 1-3, (1 no, 3 yes), how would you rate our benefits compared to your previous employer? Where was their last job?",
    name: "benefits",
    options: [...oneToThree],
    hasComments: true,
  },
  {
    type: "TextArea",
    label:
      "Can you name the companies under Milestone's umbrella?  What are their specialties?",
    name: "milestoneCompanies",
    placeholder: "Comments",
  },
  {
    type: "TextArea",
    label:
      "Tell us three things you really enjoy about your role/job here at Milestone.",
    name: "mostEnjoy",
    placeholder: "Comments",
  },
  {
    type: "TextArea",
    label:
      "Do you feel you receive proper recognition for your performance from your team leader and your department? Please explain.",
    name: "recognition",
    placeholder: "Comments",
  },
  {
    type: "TextArea",
    label:
      "Is there anything we can do to provide a more comfortable work environment for you? Please explain.",
    name: "comfortableWork",
    placeholder: "Comments",
  },
  {
    type: "TextArea",
    label: "What is one thing you like least about your role? Please explain.",
    name: "leastEnjoy",
    placeholder: "Comments",
  },
  {
    type: "TextArea",
    label:
      "How would you describe your team lead's communication on constructive feedback and positive reinforcement? Please explain. (setting, timing, tone, method)",
    name: "teamRecognition",
    placeholder: "Comments",
  },
  {
    type: "TextArea",
    label:
      "The way in which we communicate with our team members is very important to us; do you feel there is anything we can do to communicate more effectively? Please explain.",
    name: "communicateEffectively",
    placeholder: "Comments",
  },
  {
    type: "TextArea",
    label:
      "What perks does Milestone offer that makes you feel most appreciated?  (For example: breakfast, Christmas party, gym membership, etc.)",
    name: "bestPerks",
    placeholder: "Comments",
  },
  {
    type: "TextArea",
    label: "Do you understand our 401k plan and how it benefits you?",
    name: "retirementPlan",
    placeholder: "Comments",
  },
  {
    type: "TextArea",
    label:
      "How can Culture and Care better serve you? Anything we can improve on?",
    name: "cultureAndCare",
    placeholder: "Comments",
  },
  {
    type: "TextArea",
    label: "Additional comments from team member:",
    name: "additionalComments",
    placeholder: "Comments",
  },
];

export const AnnualCheckIn: React.FC<{}> = () => {
  const history = useHistory();
  const user = useAppSelector(state => state.auth.user);
  const [titles, setTitles] = useState(new Map<string, EmployeeTitle>());
  const { memberId, checkInId } =
    useParams<{ memberId: string; checkInId: string }>();
  const memberDetails = useMemberDetails(memberId);

  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const [loaded, setLoaded] = useState(false);

  const SaveButtons = (
    <SaveButtonContainer>
      <BlueButton type="submit">Submit</BlueButton>
    </SaveButtonContainer>
  );

  const { values, Schema, components } = extractQuestions(questions);

  const getCheckIn = async () => {
    //load checkIn
    const res = await Api.members.getCheckIn(memberId, checkInId);
    const checkIn = JSON.parse(res.data.review);
    setFormValues(checkIn);
    setLoaded(true);
  };

  const setValues = async () => {
    if (checkInId) {
      getCheckIn();
    } else {
      if (memberDetails) {
        if (!titles.size) {
          const res = await Api.members.getTitles();
          const titles = new Map<string, EmployeeTitle>();
          //build titles map and find the member's title
          for (const title of res.data) {
            titles.set(title.name, title);
            if (
              memberDetails.employmentInfo &&
              memberDetails.employmentInfo.employeeTitleId === title.id
            ) {
              values.position = title.name;
            }
          }
          setTitles(titles);
        }
        values.memberName = makeName(memberDetails.member);
        values.reviewerName = makeName(user);
        if (memberDetails.teamLead) {
          values.leaderName = memberDetails.teamLead.name;
        }
        values.hireDate = memberDetails.member.hireDate;
        values.reviewDate = new Date();
        setFormValues(values);
        setLoaded(true);
      }
    }
  };

  useEffect(() => {
    setValues();
  }, [checkInId, memberDetails]);

  const submitValues = async (values: any) => {
    const date = values.reviewDate;
    try {
      if (checkInId) {
        await Api.members.updateCheckIn(
          memberId,
          checkInId,
          values,
          date,
          CheckinType.ANNUAL_CHECKIN
        );
      } else {
        await Api.members.createCheckIn(
          memberId,
          values,
          date,
          CheckinType.ANNUAL_CHECKIN
        );
      }
      history.push(`/admin/members/${memberId}/checkIns`);
    } catch (error) {
      console.error("Update CheckIn", error);
      //TODO: grab error like on addteam submit
    }
  };

  return (
    <Container>
      {loaded && (
        <Formik
          initialValues={formValues}
          validationSchema={Schema}
          onSubmit={submitValues}
        >
          {() => (
            <StyledForm>
              <TopSaveWrapper>{SaveButtons}</TopSaveWrapper>

              <CheckInSection>
                <Header>Annual Review</Header>
                {components}
              </CheckInSection>
              {SaveButtons}
              <ErrorFocus />
            </StyledForm>
          )}
        </Formik>
      )}
    </Container>
  );
};
